import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Heart, ArrowRight } from "lucide-react";

const posterImage = "/mgh-poster.jpg";

/**
 * Homepage promo for the Music Giving Hope charity gala (Thu, July 16, 2026) —
 * the acoustic benefit night the evening before the festival weekend.
 * Links to the full /music-giving-hope page.
 */
const CharityGalaSection = () => {
  return (
    <section className="bg-yacht-cream py-20 md:py-28 px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl bg-yacht-blue"
          style={{ boxShadow: "0 24px 70px -20px hsla(193, 52%, 47%, 0.4)" }}
        >
          {/* Noise texture */}
          <div
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            }}
          />

          <div className="relative z-10 grid md:grid-cols-2 gap-8 md:gap-12 items-center p-8 md:p-14">
            {/* Poster */}
            <div className="flex justify-center md:justify-start order-2 md:order-1">
              <div className="relative" style={{ maxWidth: "min(360px, 78vw)" }}>
                <div
                  className="absolute -inset-6 rounded-full blur-3xl pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle, hsla(54, 93%, 65%, 0.4) 0%, transparent 72%)",
                  }}
                />
                <img
                  src={posterImage}
                  alt="Yacht Rock Detroit Unplugged — a Music Giving Hope charity gala benefiting the Detroit Youth Choir, Thursday July 16, 2026 at The Roostertail."
                  className="relative z-10 w-full h-auto block rounded-lg ring-4 ring-white/80"
                  style={{ boxShadow: "0 24px 60px -18px rgba(0,0,0,0.5)" }}
                  loading="lazy"
                />
              </div>
            </div>

            {/* Copy */}
            <div className="text-center md:text-left order-1 md:order-2">
              <p className="inline-flex items-center gap-2 text-yacht-yellow text-xs md:text-sm font-bold tracking-[0.25em] uppercase mb-4">
                <Heart className="w-4 h-4" />
                The Night Before the Festival
              </p>

              <h2 className="text-white text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.05] mb-5">
                Yacht Rock Detroit{" "}
                <span className="italic text-yacht-yellow">Unplugged</span>
              </h2>

              <p className="text-white/85 text-base md:text-lg leading-relaxed mb-6 max-w-md mx-auto md:mx-0">
                An intimate acoustic charity gala benefiting the{" "}
                <span className="text-white font-semibold">Detroit Youth Choir</span>, presented
                by Music Giving Hope — the foundation co-founded by the late Tito Jackson.
                Five stripped-down sets from yacht rock legends, cocktails, and a silent auction
                on the Detroit River.
              </p>

              <div className="flex flex-col gap-2.5 text-white/90 text-sm md:text-base mb-7 items-center md:items-start">
                <span className="flex items-center gap-2.5">
                  <Calendar className="w-4 h-4 md:w-5 md:h-5 text-yacht-yellow flex-shrink-0" />
                  Thursday, July 16, 2026 · 6:00–9:30 PM
                </span>
                <span className="flex items-center gap-2.5">
                  <MapPin className="w-4 h-4 md:w-5 md:h-5 text-yacht-yellow flex-shrink-0" />
                  The Roostertail, Detroit
                </span>
              </div>

              <Link
                to="/music-giving-hope"
                className="inline-flex items-center gap-2 bg-yacht-yellow text-yacht-charcoal font-bold px-8 py-4 rounded-full text-base md:text-lg uppercase tracking-wide hover:brightness-110 transition-all"
                style={{ boxShadow: "0 14px 40px -8px hsla(54, 93%, 49%, 0.55)" }}
              >
                Explore the Gala
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CharityGalaSection;
